<template>
  <dialog ref="dialogRef" class="modal max-h-screen">
    <div class="modal-box container flex max-w-6xl flex-col">
      <!-- Header with title and close button -->
      <div class="mb-4 flex items-baseline justify-between">
        <h3 class="text-lg font-bold">
          {{ currentTitle }}
        </h3>
        <form method="dialog">
          <button class="btn btn-ghost text-xl">&times;</button>
        </form>
      </div>

      <!-- Image container with navigation -->
      <div
        ref="imageContainer"
        class="relative mb-4 flex grow items-center justify-center overflow-hidden"
      >
        <!-- Previous button -->
        <button
          v-if="images.length > 1"
          @click="previousImage"
          class="btn btn-circle btn-ghost absolute left-0 z-10"
          :disabled="currentIndex === 0"
        >
          ❮
        </button>

        <!-- Image display with swipe effect -->
        <figure
          class="flex max-h-[calc(100vh-200px)] justify-center transition-all duration-200"
          :class="{ 'transition-none': isSwiping }"
          :style="{ transform: `translateX(${offsetX}px)`, opacity }"
        >
          <img
            :src="currentImage.url"
            :alt="currentImage.alt"
            class="max-h-full w-auto object-contain select-none"
            draggable="false"
          />
        </figure>
        <!-- Next button -->
        <button
          v-if="images.length > 1"
          @click="nextImage"
          class="btn btn-circle btn-ghost absolute right-0 z-10"
          :disabled="currentIndex === totalCount - 1"
        >
          ❯
        </button>
      </div>

      <!-- Image counter -->
      <div v-if="images.length > 1" class="text-center text-sm opacity-70">
        {{ currentIndex + 1 }} / {{ totalCount }}
      </div>

      <!-- Thumbnail strip (optional, for better navigation) -->
      <div
        v-if="images.length > 1"
        class="mt-4 flex flex-nowrap gap-2 overflow-x-auto overflow-y-hidden p-1"
        id="thumbnail_strip"
      >
        <div
          v-for="(img, index) in images"
          :id="`thumbnail_${index}`"
          :key="index"
          @click="currentIndex = index"
          :class="[
            'shrink-0 cursor-pointer rounded border-2 transition-all',
            currentIndex === index
              ? 'border-primary scale-105'
              : 'border-transparent opacity-60 hover:opacity-100',
          ]"
        >
          <img :src="img.url" :alt="img.alt" class="h-16 w-16 object-cover" />
        </div>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import type { UseSwipeDirection } from "@vueuse/core";
import { usePointerSwipe } from "@vueuse/core";
import { computed, onMounted, onUnmounted, ref } from "vue";

export interface SliderImage {
  url: string;
  alt: string;
  title: string;
}

const props = withDefaults(
  defineProps<{
    images: SliderImage[];
    initialIndex?: number;
    totalLength?: number;
  }>(),
  {
    initialIndex: 0,
  },
);

const emit = defineEmits<{
  nearEnd: [];
}>();

const currentIndex = ref(props.initialIndex);
const imageContainer = ref<HTMLElement | null>(null);
const offsetX = ref(0);
const opacity = ref(1);

// Use totalLength if provided, otherwise fall back to images.length
const totalCount = computed(() => props.totalLength || props.images.length);

const containerWidth = computed(() => imageContainer.value?.offsetWidth || 0);

function resetSwipeEffect() {
  offsetX.value = 0;
  opacity.value = 1;
}

// Pointer swipe support with smooth animation
const { distanceX, isSwiping } = usePointerSwipe(imageContainer, {
  disableTextSelect: true,
  onSwipe() {
    if (!containerWidth.value) return;

    const distance = distanceX.value;
    const swipeProgress = Math.abs(distance) / containerWidth.value;

    // Apply transform and opacity based on swipe distance
    // Reverse the offset so image follows pointer direction
    offsetX.value = -distance;
    opacity.value = Math.max(0.3, 1 - swipeProgress * 0.7);
  },
  onSwipeEnd(_: PointerEvent, direction: UseSwipeDirection) {
    if (!containerWidth.value) return;

    const swipeProgress = Math.abs(distanceX.value) / containerWidth.value;
    const threshold = 0.25; // 25% of container width to trigger navigation

    // Check if swipe is significant enough
    if (swipeProgress >= threshold) {
      // distanceX < 0 means swiping left (direction = "left") -> next image
      // distanceX > 0 means swiping right (direction = "right") -> previous image
      if (
        direction === "left" &&
        currentIndex.value < props.images.length - 1
      ) {
        // Swipe left -> next image
        nextImage();
      } else if (direction === "right" && currentIndex.value > 0) {
        // Swipe right -> previous image
        previousImage();
      }
    }

    // Reset animation
    resetSwipeEffect();
  },
});

const currentImage = computed(
  () =>
    props.images[currentIndex.value] || {
      url: "",
      alt: "",
      title: "",
    },
);

const currentTitle = computed(() => currentImage.value.title);

const nextImage = () => {
  if (currentIndex.value < props.images.length - 1) {
    currentIndex.value++;

    // Emit near-end event when user is within 10 images of the total end
    if (currentIndex.value >= props.images.length - 10) {
      emit("nearEnd");
    }
  }
  scrollThumbnail();
};

const previousImage = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
  }
  scrollThumbnail();
};

const scrollThumbnail = () => {
  const thumbnailStrip = document.getElementById("thumbnail_strip");
  const thumbnail = document.getElementById(`thumbnail_${currentIndex.value}`);
  if (thumbnailStrip && thumbnail) {
    thumbnailStrip.scrollLeft =
      thumbnail.offsetLeft -
      thumbnailStrip.offsetWidth / 2 +
      thumbnail.offsetWidth / 2;
  }
};

const dialogRef = ref<HTMLDialogElement | null>(null);

// Keyboard navigation
const handleKeyPress = (event: KeyboardEvent) => {
  if (!dialogRef.value || !dialogRef.value.open) return;

  if (event.key === "ArrowRight") {
    nextImage();
  } else if (event.key === "ArrowLeft") {
    previousImage();
  } else if (event.key === "Escape") {
    dialogRef.value.close();
  }
};

const openModal = () => {
  dialogRef.value?.showModal();
};

onMounted(() => {
  window.addEventListener("keydown", handleKeyPress);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyPress);
});

// Reset index when initialIndex prop changes
const resetIndex = () => {
  currentIndex.value = props.initialIndex || 0;
};

const setIndex = (index: number) => {
  if (index >= 0 && index < props.images.length) {
    currentIndex.value = index;
  }
};

defineExpose({
  openModal,
  setIndex,
  resetIndex,
});
</script>
